import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { useLocale, useT, type TranslationKey } from '../i18n'
import type { EntityOutboxEntityType } from '../offline/db'
import { getOfflineDb } from '../offline/dbFactory'
import {
  REFUSED_CHANGE_SCREEN,
  classifyRefusedReason,
  readRefusedChange,
  readRefusedChangeDetails,
  readRefusedRowIds,
  type RefusedChange
} from '../offline/outbox/refusedChange'
import { farmTimeZone, formatFarmDateTime } from '../utils/farmTime'

// Findings #172: one red bar, on every office screen and in Crew Mode, while
// this device holds a change the server refused (it stops the changes behind it).

const KIND_KEY: Record<EntityOutboxEntityType, TranslationKey> = {
  farm: 'syncRefused.kind.farm',
  productionUnit: 'syncRefused.kind.productionUnit',
  lot: 'syncRefused.kind.lot',
  lotMoveCommand: 'syncRefused.kind.lotMove',
  lotTransferCommand: 'syncRefused.kind.lotMove',
  lotSplitCommand: 'syncRefused.kind.lotSplit',
  lotMergeCommand: 'syncRefused.kind.lotMerge',
  waterLog: 'syncRefused.kind.waterLog',
  productionRecord: 'syncRefused.kind.productionRecord',
  feedLog: 'syncRefused.kind.feedLog',
  mortalityLog: 'syncRefused.kind.mortalityLog',
  weightSampleLog: 'syncRefused.kind.weightSampleLog',
  fishStockLog: 'syncRefused.kind.fishStockLog',
  harvestLog: 'syncRefused.kind.harvestLog',
  lotCostEvent: 'syncRefused.kind.lotCostEvent',
  inventoryItem: 'syncRefused.kind.inventoryItem',
  task: 'syncRefused.kind.task',
  mortalityMatrix: 'syncRefused.kind.matrix',
  sfrMatrix: 'syncRefused.kind.matrix'
}

const SCREEN_KEY: Record<string, TranslationKey> = {
  '/edit-farm': 'syncRefused.screen.editFarm',
  '/lots': 'syncRefused.screen.lots',
  '/water': 'syncRefused.screen.water',
  '/database': 'syncRefused.screen.database',
  '/feed': 'syncRefused.screen.feed',
  '/biomass': 'syncRefused.screen.biomass',
  '/costs': 'syncRefused.screen.costs',
  '/inventory': 'syncRefused.screen.inventory',
  '/tasks': 'syncRefused.screen.tasks',
  '/mortality-matrix': 'syncRefused.screen.matrix',
  '/sfr': 'syncRefused.screen.matrix'
}

const POLL_MS = 5000

export const useRefusedChange = (): { refused: RefusedChange | null; unitNames: Map<string, string> } => {
  const [refused, setRefused] = useState<RefusedChange | null>(null)
  const [unitNames, setUnitNames] = useState<Map<string, string>>(new Map())

  useEffect(() => {
    let cancelled = false
    const check = () => {
      void readRefusedChange().then(async (next) => {
        if (cancelled) return
        setRefused(next)
        if (!next) return
        try {
          const farms = await getOfflineDb().farms.toArray()
          if (cancelled) return
          const names = new Map<string, string>()
          farms.forEach((farm) => (farm.productionUnits ?? []).forEach((unit) => {
            if (unit?.id) names.set(String(unit.id), String(unit.name ?? unit.id))
          }))
          setUnitNames(names)
        } catch {
          // names are optional
        }
      })
    }
    check()
    const timer = window.setInterval(check, POLL_MS)
    window.addEventListener('focus', check)
    window.addEventListener('online', check)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      window.removeEventListener('focus', check)
      window.removeEventListener('online', check)
    }
  }, [])

  return { refused, unitNames }
}

/** Row ids (record or production row) of refused records of these types, refreshed like the bar. */
export const useRefusedRowIds = (entityTypes: readonly EntityOutboxEntityType[]): Set<string> => {
  const [ids, setIds] = useState<Set<string>>(new Set())
  const typesKey = entityTypes.join(',')

  useEffect(() => {
    let cancelled = false
    const types = typesKey.split(',') as EntityOutboxEntityType[]
    const check = () => {
      void readRefusedRowIds(types).then((next) => {
        if (cancelled) return
        setIds((current) => (current.size === next.size && [...next].every((id) => current.has(id)) ? current : next))
      })
    }
    check()
    const timer = window.setInterval(check, POLL_MS)
    window.addEventListener('focus', check)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      window.removeEventListener('focus', check)
    }
  }, [typesKey])

  return ids
}

/** "No enviado" next to a row the server refused. */
export function RefusedRowTag({ show, testId }: { show: boolean; testId?: string }) {
  const t = useT()
  if (!show) return null
  return (
    <span
      data-testid={testId ?? 'sync-refused-row-tag'}
      style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 999, background: '#fee2e2', color: '#991b1b', fontSize: 12, fontWeight: 700 }}
    >
      {t('syncRefused.rowTag')}
    </span>
  )
}

export default function RefusedChangeBanner({ variant }: { variant: 'office' | 'crew' }) {
  const t = useT()
  const locale = useLocale()
  const { refused, unitNames } = useRefusedChange()
  if (!refused) return null

  const { entry, waitingBehind } = refused
  const details = readRefusedChangeDetails(entry)
  const unitName = details.productionUnitId ? unitNames.get(details.productionUnitId) ?? '' : ''
  const when = details.recordedAt
    ? formatFarmDateTime(details.recordedAt, farmTimeZone(details.farmId || null), { locale })
    : ''
  const what = [t(KIND_KEY[entry.entityType] ?? 'syncRefused.kind.other'), unitName, when, details.lotLabel || details.name]
    .filter(Boolean)
    .join(' · ')

  const reason = classifyRefusedReason(entry.lastError)
  const reasonText = reason.kind === 'unitMismatch'
    ? t('syncRefused.reason.unitMismatch', { lot: details.lotLabel || '—', unit: unitName || '—' })
    : reason.kind === 'lotNotActive'
      ? t('syncRefused.reason.lotNotActive', { lot: details.lotLabel || '—' })
      : reason.kind === 'lotNotFound'
        ? t('syncRefused.reason.lotNotFound')
        : t('syncRefused.reason.other', { message: reason.message || '—' })

  const screenPath = REFUSED_CHANGE_SCREEN[entry.entityType] ?? '/home'
  const screenName = t(SCREEN_KEY[screenPath] ?? 'syncRefused.screen.home')

  return (
    <div
      role="alert"
      data-testid="sync-refused-banner"
      style={{
        margin: variant === 'crew' ? '8px 12px 0' : '0 0 12px',
        padding: '10px 12px',
        borderRadius: 10,
        background: '#fef2f2',
        border: '1px solid #fca5a5',
        color: '#991b1b',
        fontSize: 14,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap'
      }}
    >
      <div style={{ flex: '1 1 280px', display: 'grid', gap: 4 }}>
        <strong>
          {waitingBehind > 0
            ? t('syncRefused.title.withWaiting', { count: String(waitingBehind) })
            : t('syncRefused.title.alone')}
        </strong>
        <span data-testid="sync-refused-what">{what}</span>
        <span data-testid="sync-refused-reason">{reasonText}</span>
        <span>
          {variant === 'crew'
            ? t('syncRefused.action.crew')
            : t('syncRefused.action.office', { screen: screenName })}
        </span>
      </div>
      {variant === 'office' ? (
        <Link
          to={screenPath}
          data-testid="sync-refused-open"
          style={{ padding: '8px 14px', borderRadius: 8, background: '#b91c1c', color: '#ffffff', fontWeight: 700, textDecoration: 'none' }}
        >
          {t('syncRefused.open')}
        </Link>
      ) : null}
    </div>
  )
}
